import SavedFilters,{GroupedRows} from '../../components/SavedFilters';
import React, { useEffect, useMemo, useState } from 'react';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet, rrhhCorePost } from './rrhhCoreApi';
import {
  OPERATION_TARIFF_TYPES,
  normalizeOperationTariffCategory,
  normalizeOperationTariffType,
  operationBandsForTariff,
  operationCategoriesForType,
} from './rrhhUtils';
import './RRHHNative.css';

const EMPTY_TARIFF = {
  effective_date: '',
  categoria: 'C1', tipo: 'corporativo', hasta_4hs: 0, de_4_a_8hs: 0, de_8_a_12hs: 0, bands: [], activo: true, personnel_ids: [],
};

function personName(person) {
  return person.nickname || [person.first_name, person.last_name].filter(Boolean).join(' ') || `Personal #${person.id}`;
}

function amount(value) {
  return Number(value || 0).toLocaleString('es-UY', { style: 'currency', currency: 'UYU', maximumFractionDigits: 0 });
}

function typeLabel(type) {
  const normalized = normalizeOperationTariffType(type) || String(type || '').trim();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Sin tipo';
}

function legacyValuesForTariff(tariff) {
  const bands = operationBandsForTariff(tariff);
  return {
    hasta_4hs: Number(bands[0]?.value || 0),
    de_4_a_8hs: Number(bands[1]?.value || 0),
    de_8_a_12hs: Number(bands[2]?.value || 0),
    bands,
  };
}

function withTariffDefaults(tariff = {}) {
  const tipo = normalizeOperationTariffType(tariff.tipo) || EMPTY_TARIFF.tipo;
  const categories = operationCategoriesForType(tipo);
  const categoria = categories.includes(normalizeOperationTariffCategory(tariff.categoria))
    ? normalizeOperationTariffCategory(tariff.categoria)
    : categories[0] || EMPTY_TARIFF.categoria;
  const normalized = { ...EMPTY_TARIFF, ...tariff, tipo, categoria };
  return { ...normalized, ...legacyValuesForTariff(normalized) };
}

export default function TarifasOperacionesPage() {
  const [filters,setFilters]=useState({search:'',type:'',state:'',__group:''});
  const [tariffs, setTariffs] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function refresh() {
    setLoading(true); setError('');
    try {
      const data = await rrhhCoreGet('/operacion-tarifas/admin');
      setTariffs(data.tariffs || []);
      setPersonnel(data.personnel || []);
    } catch (err) { setError(err.message || 'No se pudieron cargar las tarifas.'); }
    finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);
  const assignedCount = useMemo(() => new Set(editing?.personnel_ids || []).size, [editing]);
  const personnelById = useMemo(() => {
    const map = new Map();
    personnel.forEach((person) => map.set(Number(person.id), person));
    return map;
  }, [personnel]);

  function openNew() { setEditing(withTariffDefaults({ personnel_ids: [] })); setError(''); }
  function openEdit(tariff) { setEditing(withTariffDefaults({ ...tariff, personnel_ids: (tariff.personnel_ids || []).map(Number) })); setError(''); }
  function update(field, value) { setEditing((current) => ({ ...current, [field]: value })); }
  function updateIdentity(field, value) {
    setEditing((current) => {
      let next = { ...current, [field]: value };
      if (field === 'tipo') {
        const tipo = normalizeOperationTariffType(value) || EMPTY_TARIFF.tipo;
        next = { ...next, tipo, categoria: operationCategoriesForType(tipo)[0] || '' };
      }
      if (field === 'categoria') next = { ...next, categoria: normalizeOperationTariffCategory(value) };
      return withTariffDefaults(next);
    });
  }
  function togglePerson(id) {
    setEditing((current) => {
      const ids = new Set((current.personnel_ids || []).map(Number));
      if (ids.has(id)) ids.delete(id); else ids.add(id);
      return { ...current, personnel_ids: [...ids] };
    });
  }
  function updateBandValue(bandName, value) {
    setEditing((current) => {
      const bands = operationBandsForTariff(current).map((band) => (
        band.band === bandName ? { ...band, value: Number(value || 0) } : band
      ));
      return withTariffDefaults({ ...current, bands });
    });
  }

  async function save(event) {
    event.preventDefault();
    if (!editing) return;
    setSaving(true); setError('');
    try {
      await rrhhCorePost('/operacion-tarifas/admin', withTariffDefaults(editing));
      window.dispatchEvent(new Event('payroll:updated'));setEditing(null); setNotice('Tarifa guardada'); await refresh();
    } catch (err) { setError(err.message || 'No se pudo guardar la tarifa.'); }
    finally { setSaving(false); }
  }

  async function archive(id) {
    if (!window.confirm('¿Desactivar esta tarifa? Se conservará en las operaciones históricas.')) return;
    const effective_date=window.prompt('¿Desde qué día se desactiva? (AAAA-MM-DD)');if(!effective_date)return;
    setSaving(true); setError('');
    try { await rrhhCorePost(`/operacion-tarifas/admin/${id}/archive`, {effective_date}); setNotice('Tarifa desactivada'); await refresh(); }
    catch (err) { setError(err.message || 'No se pudo desactivar la tarifa.'); }
    finally { setSaving(false); }
  }

  function namesForTariff(tariff) {
    return (tariff.personnel_ids || [])
      .map((id) => personnelById.get(Number(id)))
      .filter(Boolean)
      .map(personName)
      .sort((a, b) => a.localeCompare(b, 'es'));
  }

  const visible=tariffs.filter(t=>(!filters.type||t.tipo===filters.type)&&(!filters.state||(filters.state==='active')===t.activo)&&[t.categoria,t.tipo,...namesForTariff(t)].join(' ').toLocaleLowerCase().includes(filters.search.toLocaleLowerCase()));
  const actions = <button className="btn btn-primary" type="button" onClick={openNew}>Nueva tarifa</button>;
  return <RRHHNativeShell title="Tarifas de Operaciones" subtitle="Categorías, franjas de valor y personas habilitadas para operar." actions={actions}>
    <SavedFilters scope="operation_tariffs" value={filters} onChange={setFilters} fields={[{key:'type',label:'Tipo'},{key:'state',label:'Estado',format:v=>v==='active'?'Activa':'Inactiva'}]} groups={[{value:'tipo',label:'Tipo'},{value:'activo',label:'Estado'}]}>
    <label>Tipo<select className="form-input" value={filters.type} onChange={e=>setFilters({...filters,type:e.target.value})}><option value="">Todos</option>{OPERATION_TARIFF_TYPES.map(t=><option key={t} value={t}>{typeLabel(t)}</option>)}</select></label>
    <label>Estado<select className="form-input" value={filters.state} onChange={e=>setFilters({...filters,state:e.target.value})}><option value="">Todas</option><option value="active">Activa</option><option value="inactive">Inactiva</option></select></label>
    </SavedFilters>
    {error && <div className="alert alert-error">{error}</div>}
    {notice && <div className="alert alert-success">{notice}</div>}
    {editing && <section className="rrhh-native-card rrhh-tariff-editor">
      <div className="rrhh-native-toolbar"><div><h2>{editing.id ? 'Editar tarifa' : 'Nueva tarifa'}</h2><p>La tarifa se habilita únicamente para las personas seleccionadas.</p></div><button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>Cancelar</button></div>
      <form className="rrhh-native-form" onSubmit={save}>
        <div className="rrhh-native-grid rrhh-tariff-fields">
          <label>Tipo
            <select value={editing.tipo} onChange={(event) => updateIdentity('tipo', event.target.value)} required>
              {OPERATION_TARIFF_TYPES.map((type) => <option key={type} value={type}>{typeLabel(type)}</option>)}
            </select>
          </label>
          <label>Categoría
            <select value={editing.categoria} onChange={(event) => updateIdentity('categoria', event.target.value)} required>
              {operationCategoriesForType(editing.tipo).map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label className="rrhh-tariff-active"><input type="checkbox" checked={editing.activo !== false} onChange={(event) => update('activo', event.target.checked)} /> Activa</label>
        </div>
        <label>¿Desde qué día tiene vigencia este cambio?<input className="form-input" type="date" required value={editing.effective_date||''} onChange={e=>update('effective_date',e.target.value)}/></label><p>Se conserva lo ya liquidado y validado. Las operaciones mantienen la categoría que registraron.</p>
        <section className="rrhh-tariff-matrix" aria-label="Franjas de la tarifa">
          {operationBandsForTariff(editing).map((band) => (
            <div key={band.band} className="rrhh-tariff-matrix-row">
              <span>{band.band}</span>
              <label>
                <span className="sr-only">Valor {band.band}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={Number(band.value || 0)}
                  onChange={(event) => updateBandValue(band.band, event.target.value)}
                />
              </label>
            </div>
          ))}
        </section>
        <section className="rrhh-tariff-personnel"><div className="rrhh-native-toolbar"><div><h3>Personal habilitado</h3><p>{assignedCount} personas seleccionadas</p></div></div><div className="rrhh-tariff-personnel-list">{personnel.map((person) => { const checked = (editing.personnel_ids || []).map(Number).includes(person.id); return <label key={person.id} className={checked ? 'selected' : ''}><input type="checkbox" checked={checked} onChange={() => togglePerson(person.id)} /><span>{personName(person)}</span><small>{person.operational_role || 'Sin rol operativo'}</small></label>; })}</div></section>
        <div className="rrhh-native-toolbar-group"><button className="btn btn-primary" disabled={saving} type="submit">Guardar tarifa</button></div>
      </form>
    </section>}
    {loading ? <div className="rrhh-native-card rrhh-native-empty">Cargando tarifas...</div> : <section className="rrhh-native-card rrhh-native-table-wrap"><table className="rrhh-native-table"><thead><tr><th>Tipo</th><th>Categoría</th><th>Franjas</th><th>Personal</th><th>Estado</th><th aria-label="Acciones" /></tr></thead><tbody><GroupedRows rows={visible} groupBy={filters.__group} columns={6} groupValue={(r,k)=>k==='activo'?(r.activo?'Activa':'Inactiva'):typeLabel(r[k])} renderRow={(tariff) => {
      const names = namesForTariff(tariff);
      return <tr key={tariff.id}><td>{typeLabel(tariff.tipo)}</td><td><strong>{normalizeOperationTariffCategory(tariff.categoria) || tariff.categoria}</strong></td><td><div className="rrhh-tariff-band-list">{operationBandsForTariff(tariff).map((band) => <span key={band.band}><small>{band.band}</small><strong>{amount(band.value)}</strong></span>)}</div></td><td><div className="rrhh-tariff-person-chip-list">{names.length ? names.map((name) => <span key={name}>{name}</span>) : <em>Sin personal</em>}</div></td><td><span className={`rrhh-tariff-state ${tariff.activo ? 'active' : ''}`}>{tariff.activo ? 'Activa' : 'Inactiva'}</span></td><td><div className="rrhh-native-toolbar-group"><button className="btn btn-secondary" type="button" onClick={() => openEdit(tariff)}>Editar</button>{tariff.activo && <button className="btn btn-danger" type="button" disabled={saving} onClick={() => archive(tariff.id)}>Desactivar</button>}</div></td></tr>;
    }}/>{!visible.length && <tr><td colSpan="6" className="rrhh-native-empty">No hay tarifas de operación configuradas.</td></tr>}</tbody></table></section>}
  </RRHHNativeShell>;
}
